import { useQuery, useQueryClient } from '@tanstack/react-query';

import { API } from '@/shared/api';
import { getGoogleAdsIdentifiers, GoogleAdsIdentifiers } from '@/shared/lib/attribution';
import { User } from '@/shared/types/User';

import { GOOGLE_ADS_ATTRIBUTION_QUERY_KEY } from '../lib/constants';
import { USER_QUERY_KEYS } from '../lib/userQueryKeys';

const hasSameIdentifiers = (left: User['gads'], right: GoogleAdsIdentifiers) =>
  left?.gclid === right.gclid && left.gbraid === right.gbraid && left.wbraid === right.wbraid;

export const useSyncGoogleAdsAttribution = (user?: User) => {
  const queryClient = useQueryClient();
  const userId = user?.id;

  useQuery({
    enabled: Boolean(userId),
    queryFn: async () => {
      if (!userId) return null;
      const identifiers = getGoogleAdsIdentifiers();
      if (!identifiers) return null;

      const current = queryClient.getQueryData<User>(USER_QUERY_KEYS.userData);
      if (!current || current.id !== userId || hasSameIdentifiers(current.gads, identifiers)) return null;

      const response = await API.saveGoogleAdsAttribution({
        ...identifiers,
        captured_at: new Date().toISOString(),
      });
      queryClient.setQueryData<User>(USER_QUERY_KEYS.userData, (cachedUser) =>
        cachedUser?.id === userId ? { ...cachedUser, gads: response } : cachedUser,
      );
      return response;
    },
    queryKey: [...GOOGLE_ADS_ATTRIBUTION_QUERY_KEY, userId],
    refetchInterval: 60_000,
    refetchOnWindowFocus: 'always',
    retry: false,
  });
};
